/**
 * Product Bundle (New) — bundle stepper custom element.
 *
 * Hosts the `[data-bn-stepper]` element and owns:
 *   - Step navigation (Next / Prev / Review).
 *   - Variant selection per step + recommendation matching.
 *   - Hover preview, review-panel edit overlays, ATC enable/disable state.
 *   - Document-level click delegations for accordions, ingredients modal, and
 *     notify modal — cached + removed on disconnect so Theme Editor reloads
 *     don't leak handlers.
 *
 * The ATC button and scroll-to-stepper button live OUTSIDE the stepper but
 * inside the same section. They're queried via `document.querySelector` and
 * their listeners are direct (per-button), so they auto-clean when the section
 * is removed.
 */

/** Reject color values that could break out of the inline-style context. */
function bnIsSafeCssColor(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (!v || v.length > 64) return false;
  if (/[{};<>\\"'`\n\r]/.test(v)) return false;
  if (v.indexOf('/*') !== -1 || v.indexOf('*/') !== -1) return false;
  return true;
}

function bnBuildSwatchStyle(bg) {
  // Some variants' `swatch_bg` is an object (image/color metaobject) rather than
  // a string. Guard against it — calling .indexOf on an object throws and was
  // aborting selectSwatch()/getSwatchStyle() for swapped Face Pencil shades.
  if (bg && typeof bg === 'object') {
    bg = bg.image || bg.color || bg.src || bg.value || '';
  }
  if (!bg || typeof bg !== 'string') return '';
  if (bg.indexOf('http') === 0 || bg.indexOf('//') === 0) {
    return `background-image:url(${bg});background-size:cover;`;
  }
  return `background-color:${bg};`;
}

function bnEscapeAttr(s) {
  return (s || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function bnEscapeHtml(s) {
  return (s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

class ProductBundleNew extends HTMLElement {
  /** @type {number} */
  totalSteps = 0;

  /** @type {number | 'review'} */
  currentStep = 1;

  /** @type {Object<number, {variantId: number, title: string, shade_desc: string, swatchStyle: string, image: string}>} */
  selections = {};

  /** @type {Object<number, object>} parsed product JSON per step */
  productData = {};

  /** @type {Object<number, Object<string, string>>} step -> variantId -> rendered style */
  domSwatchStyles = {};

  /** @type {Object<string, string>} settings.swatch_colors lookup */
  settingsSwatchMap = {};

  constructor() {
    super();
    this._onStepperClick = this._onStepperClick.bind(this);
    this._onStepperMouseover = this._onStepperMouseover.bind(this);
    this._onStepperMouseout = this._onStepperMouseout.bind(this);
    this._onPrevClick = this._onPrevClick.bind(this);
    this._onNextClick = this._onNextClick.bind(this);
    this._onScrollClick = this._onScrollClick.bind(this);
    this._onDocNotifyClick = this._onDocNotifyClick.bind(this);
    this._onAjaxStart = this._onAjaxStart.bind(this);
    this._onAjaxEnd = this._onAjaxEnd.bind(this);
    this._onAtcClick = this._onAtcClick.bind(this);
  }

  connectedCallback() {
    this.totalSteps = parseInt(this.dataset.totalSteps, 10) || 0;
    if (this.totalSteps === 0) return;

    this._cacheDomRefs();
    this._parseProductData();
    this._parseSettingsSwatchMap();
    this._buildDomSwatchStyles();
    this._autoSelectSingleVariantPanels();
    this._bindListeners();

    this.showStep(1);
    this.updateAtcState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onStepperClick);
    this.removeEventListener('mouseover', this._onStepperMouseover);
    this.removeEventListener('mouseout', this._onStepperMouseout);
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrevClick);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNextClick);
    if (this.scrollBtn) this.scrollBtn.removeEventListener('click', this._onScrollClick);
    if (this.atcBtn) this.atcBtn.removeEventListener('click', this._onAtcClick);
    document.removeEventListener('click', this._onDocNotifyClick, true);
    document.removeEventListener('liquid-ajax-cart:request-start', this._onAjaxStart);
    document.removeEventListener('liquid-ajax-cart:request-end', this._onAjaxEnd);
  }

  /* ───────────────────────── Setup ───────────────────────── */

  _cacheDomRefs() {
    this.panels = this.querySelectorAll('[data-bn-panel]');
    this.stepsEl = this.querySelector('[data-bn-steps]');
    this.indicators = this.querySelectorAll('[data-bn-step-indicator]');
    this.prevBtn = this.querySelector('[data-bn-prev]');
    this.nextBtn = this.querySelector('[data-bn-next]');
    this.reviewPanel = this.querySelector('[data-bn-panel="review"]');

    // These two live OUTSIDE the stepper but inside the same section.
    const root = this.closest('product-info');
    this.atcBtn = root?.querySelector('[data-bn-atc]') ?? null;
    this.scrollBtn = root?.querySelector('[data-bn-scroll-to-stepper]') ?? null;
    this.atcForm = this.atcBtn?.closest('ajax-cart-product-form') ?? null;
  }

  _parseProductData() {
    for (let i = 1; i <= this.totalSteps; i++) {
      const el = this.querySelector(`[data-bn-product="${i}"]`);
      if (!el) continue;
      try {
        this.productData[i] = JSON.parse(el.textContent);
      } catch {
        /* skip malformed entries */
      }
    }
    // Stable snapshot of each step's ORIGINAL product. `productData[step]` is
    // reassigned during a cross-product panel swap (deep Face Pencil shades turn
    // step 2 into a 2nd Face Pencil); this snapshot lets us resolve which product
    // owns a recommended variant and restore the original panel afterwards.
    this._origProductData = Object.assign({}, this.productData);
  }

  _parseSettingsSwatchMap() {
    const scEl = document.querySelector('script[id^="product-swatch-colors-"]');
    if (!scEl) return;
    try {
      const raw = JSON.parse(scEl.textContent);
      if (typeof raw !== 'string') return;
      raw.split('\n').forEach((line) => {
        line = line.trim();
        if (!line) return;
        let sep = line.indexOf('==');
        if (sep === -1) sep = line.indexOf('=');
        if (sep === -1) return;
        const name = line.substring(0, sep).trim().toLowerCase();
        const color = line.substring(sep + (line.charAt(sep + 1) === '=' ? 2 : 1)).trim();
        if (name && color && bnIsSafeCssColor(color)) this.settingsSwatchMap[name] = color;
      });
    } catch {
      /* skip */
    }
  }

  /**
   * Snapshot the rendered swatch style per `step -> variantId`. This captures
   * ALL the Liquid fallbacks (metafield image, metafield color, value.swatch,
   * featured_image, settings lookup) without re-deriving them in JS.
   */
  _buildDomSwatchStyles() {
    for (let s = 1; s <= this.totalSteps; s++) {
      this.domSwatchStyles[s] = {};
      const btns = this.querySelectorAll(`[data-bn-swatches="${s}"] [data-bn-swatch]`);
      btns.forEach((btn) => {
        const vid = btn.dataset.bnSwatch;
        const colorSpan = btn.querySelector('.ph-swatch__color');
        if (colorSpan) {
          this.domSwatchStyles[s][vid] = colorSpan.getAttribute('style') || '';
        }
      });
    }
  }

  _autoSelectSingleVariantPanels() {
    this.panels.forEach((panel) => {
      const autoId = panel.dataset.autoVariant;
      if (!autoId) return;
      const step = parseInt(panel.dataset.bnPanel, 10);
      if (Number.isNaN(step)) return;
      const pd = this.productData[step];
      const v = pd && pd.variants ? pd.variants.find((x) => x.id === parseInt(autoId, 10)) : null;
      this.selections[step] = {
        variantId: parseInt(autoId, 10),
        title: v ? v.title : '',
        shade_desc: v ? v.shade_desc : '',
        swatchStyle: this.getSwatchStyle(step, autoId),
        image: v ? v.featured_image : '',
      };
    });
  }

  _bindListeners() {
    this.addEventListener('click', this._onStepperClick);
    this.addEventListener('mouseover', this._onStepperMouseover);
    this.addEventListener('mouseout', this._onStepperMouseout);
    if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrevClick);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNextClick);
    if (this.scrollBtn) this.scrollBtn.addEventListener('click', this._onScrollClick);
    if (this.atcBtn) this.atcBtn.addEventListener('click', this._onAtcClick);

    // Document-level delegation for notify modal (sibling outside stepper).
    document.addEventListener('click', this._onDocNotifyClick, true);
    document.addEventListener('liquid-ajax-cart:request-start', this._onAjaxStart);
    document.addEventListener('liquid-ajax-cart:request-end', this._onAjaxEnd);
  }

  /* ───────────────────────── Helpers ───────────────────────── */

  getSwatchStyle(step, variantId) {
    const key = String(variantId);
    // 1. DOM-rendered style (most accurate — includes all Liquid fallbacks)
    const lookup = this.domSwatchStyles[step];
    if (lookup && lookup[key]) return lookup[key];
    // 1b. Cross-product swap: a swapped step (e.g. step 2 → The Face Pencil)
    // shows variants that were rendered under their ORIGINAL step. Their
    // `swatch_bg` JSON can be an unusable metafield object, so reuse the
    // rendered style from whichever step owns this variant. Variant IDs are
    // globally unique, so this can't collide.
    for (const s in this.domSwatchStyles) {
      const m = this.domSwatchStyles[s];
      if (m && m[key]) return m[key];
    }
    // 2. JSON swatch_bg from Liquid
    const pd = this.productData[step];
    const v = pd && pd.variants ? pd.variants.find((x) => x.id === variantId) : null;
    if (v && v.swatch_bg) return bnBuildSwatchStyle(v.swatch_bg);
    // 3. Settings swatch_colors lookup by variant title
    if (v && v.title) {
      const titleLower = v.title.toLowerCase().trim();
      const settingsColor = this.settingsSwatchMap[titleLower];
      if (settingsColor && bnIsSafeCssColor(settingsColor)) return bnBuildSwatchStyle(settingsColor);
    }
    return 'background-color:#ccc;';
  }

  /* ───────────────────────── Step navigation ───────────────────────── */

  showStep(step) {
    if (step === 'review') {
      this.panels.forEach((p) => {
        p.style.display = 'none';
      });
      if (this.reviewPanel) this.reviewPanel.style.display = '';
      if (this.stepsEl) this.stepsEl.style.display = 'none';
      this.indicators.forEach((ind) => {
        ind.classList.remove('is-active');
        ind.classList.add('is-completed');
      });
      this.populateReview();
      this.updateNav('review');
      this.currentStep = 'review';
      return;
    }
    this.currentStep = step;
    if (this.stepsEl) this.stepsEl.style.display = '';
    this.panels.forEach((p) => {
      if (p.dataset.bnPanel === 'review') {
        p.style.display = 'none';
      } else {
        p.style.display = parseInt(p.dataset.bnPanel, 10) === step ? '' : 'none';
      }
    });
    this.indicators.forEach((ind) => {
      const idx = parseInt(ind.dataset.bnStepIndicator, 10);
      ind.classList.remove('is-active', 'is-completed');
      if (idx < step) ind.classList.add('is-completed');
      else if (idx === step) ind.classList.add('is-active');
    });
    this.updateNav(step);
    this.updateRecommended(step);
  }

  updateNav(step) {
    if (step === 'review') {
      if (this.prevBtn) this.prevBtn.disabled = false;
      if (this.nextBtn) this.nextBtn.style.display = 'none';
      return;
    }
    if (this.nextBtn) this.nextBtn.style.display = '';
    if (this.prevBtn) this.prevBtn.disabled = step <= 1;
    if (this.nextBtn) this.nextBtn.disabled = !this.selections[step];
    const nextLabel = this.nextBtn?.querySelector('span');
    if (nextLabel) {
      nextLabel.textContent = 'NEXT';
    }
  }

  /* ───────────────────────── Recommendations ───────────────────────── */

  /* ─── Cross-product panel swap ───
   * Live Main: when a DEEP Face Pencil shade is selected in step 1, step 2's
   * whole panel becomes "The Face Pencil" (title, description, image and the
   * full 25-shade swatch grid) because that shade "doubles as a neutralizing
   * pencil". For lighter shades step 2 stays The Neutralizer Pencil.
   *
   * The recommended product (The Face Pencil) is already fully rendered as
   * step 1, so we mirror its panel content into the target step and point
   * `productData[step]` at it so selection / cart work natively. The original
   * panel is captured once and restored when the recommendation is same-product
   * again.
   */
  _applyStepProductSwap(step) {
    const panel = this.querySelector(`[data-bn-panel="${step}"]`);
    if (!panel) return;

    const stepOneSel = this.selections[1];
    const explicitIds = stepOneSel ? stepOneSel[`recommended_variant_ids_step_${step}`] || [] : [];

    // Which ORIGINAL product owns the (first) recommended variant?
    const origData = this._origProductData || this.productData;
    let sourceStep = null;
    for (let i = 0; i < explicitIds.length && !sourceStep; i++) {
      const id = explicitIds[i];
      Object.keys(origData).forEach((s) => {
        if (sourceStep) return;
        const pd = origData[s];
        if (pd && pd.variants && pd.variants.some((v) => v.id === id)) sourceStep = parseInt(s, 10);
      });
    }
    const shouldSwap = !!sourceStep && sourceStep !== step;

    // Capture the pristine panel once.
    if (!this._origStep) this._origStep = {};
    if (!this._origStep[step]) {
      const titleEl = panel.querySelector('.bn-stepper__product-title');
      const descEl = panel.querySelector('.bn-stepper__product-desc');
      const swEl = panel.querySelector(`[data-bn-swatches="${step}"]`);
      const imgEl = panel.querySelector('[data-bn-panel-img]');
      const indLabel = this.querySelector(`[data-bn-step-indicator="${step}"] .bn-stepper__step-label`);
      this._origStep[step] = {
        title: titleEl ? titleEl.textContent : '',
        descHTML: descEl ? descEl.innerHTML : null,
        swatchesHTML: swEl ? swEl.innerHTML : null,
        imgSrc: imgEl ? imgEl.getAttribute('src') : null,
        indLabelHTML: indLabel ? indLabel.innerHTML : null,
      };
    }

    if (shouldSwap) {
      if (panel.dataset.bnSwappedTo === String(sourceStep)) return;
      const srcPanel = this.querySelector(`[data-bn-panel="${sourceStep}"]`);
      const srcProduct = origData[sourceStep];
      this.productData[step] = srcProduct; // selection / cart now use source product

      const titleEl = panel.querySelector('.bn-stepper__product-title');
      if (titleEl && srcProduct) titleEl.textContent = String(srcProduct.title || '').toUpperCase();

      const descEl = panel.querySelector('.bn-stepper__product-desc');
      const srcDescEl = srcPanel && srcPanel.querySelector('.bn-stepper__product-desc');
      if (descEl && srcDescEl) descEl.innerHTML = srcDescEl.innerHTML;

      const imgEl = panel.querySelector('[data-bn-panel-img]');
      const srcImgEl = srcPanel && srcPanel.querySelector('[data-bn-panel-img]');
      if (imgEl && srcImgEl) imgEl.setAttribute('src', srcImgEl.getAttribute('src'));

      const swEl = panel.querySelector(`[data-bn-swatches="${step}"]`);
      const srcSwEl = srcPanel && srcPanel.querySelector(`[data-bn-swatches="${sourceStep}"]`);
      if (swEl && srcSwEl) {
        swEl.innerHTML = srcSwEl.innerHTML;
        // Retarget the cloned swatches to THIS step and clear any active state.
        swEl.querySelectorAll('[data-bn-step]').forEach((b) => b.setAttribute('data-bn-step', String(step)));
        swEl.querySelectorAll('.ph-swatch--active').forEach((b) => b.classList.remove('ph-swatch--active'));
      }
      // Top-stepper indicator label also reflects the swapped product on Main.
      const indLabel = this.querySelector(`[data-bn-step-indicator="${step}"] .bn-stepper__step-label`);
      const srcIndLabel = this.querySelector(`[data-bn-step-indicator="${sourceStep}"] .bn-stepper__step-label`);
      if (indLabel && srcIndLabel) indLabel.innerHTML = srcIndLabel.innerHTML;

      panel.dataset.bnSwappedTo = String(sourceStep);
      // Clear any prior selection (it referenced the old product).
      delete this.selections[step];
      const hidden = document.querySelector(`[data-bn-hidden-input="${step}"]`);
      if (hidden) hidden.value = '';
    } else if (panel.dataset.bnSwappedTo) {
      // Restore the original (Neutralizer) panel.
      const orig = this._origStep[step];
      this.productData[step] = origData[step];
      const titleEl = panel.querySelector('.bn-stepper__product-title');
      if (titleEl) titleEl.textContent = orig.title;
      const descEl = panel.querySelector('.bn-stepper__product-desc');
      if (descEl && orig.descHTML != null) descEl.innerHTML = orig.descHTML;
      const swEl = panel.querySelector(`[data-bn-swatches="${step}"]`);
      if (swEl && orig.swatchesHTML != null) swEl.innerHTML = orig.swatchesHTML;
      const imgEl = panel.querySelector('[data-bn-panel-img]');
      if (imgEl && orig.imgSrc != null) imgEl.setAttribute('src', orig.imgSrc);
      const indLabel = this.querySelector(`[data-bn-step-indicator="${step}"] .bn-stepper__step-label`);
      if (indLabel && orig.indLabelHTML != null) indLabel.innerHTML = orig.indLabelHTML;
      delete panel.dataset.bnSwappedTo;
      delete this.selections[step];
      const hidden = document.querySelector(`[data-bn-hidden-input="${step}"]`);
      if (hidden) hidden.value = '';
    }
  }

  updateRecommended(step) {
    this._applyStepProductSwap(step);
    const container = this.querySelector(`[data-bn-recommendations="${step}"]`);
    if (!container) return;

    const panel = this.querySelector(`[data-bn-panel="${step}"]`);
    if (!panel || panel.dataset.autoVariant) {
      container.innerHTML = '';
      return;
    }

    // Use the FIRST step with a shade_desc as the primary reference (Face Pencil),
    // not the immediately previous step. Fall back to nearest previous if none has desc.
    let prevSel = null;
    for (let s = 1; s < step; s++) {
      if (this.selections[s] && this.selections[s].shade_desc) {
        prevSel = this.selections[s];
        break;
      }
    }
    if (!prevSel) {
      for (let s2 = step - 1; s2 >= 1; s2--) {
        if (this.selections[s2] && this.selections[s2].title) {
          prevSel = this.selections[s2];
          break;
        }
      }
    }
    if (!prevSel) {
      container.innerHTML = '';
      return;
    }

    const curPd = this.productData[step];
    if (!curPd || !curPd.variants || curPd.variants.length === 0) {
      container.innerHTML = '';
      return;
    }

    const available = curPd.variants.filter((v) => v.available);
    if (available.length === 0) {
      container.innerHTML = '';
      return;
    }

    const stepOneSel = this.selections[1] || null;
    const explicitIds = stepOneSel ? stepOneSel[`recommended_variant_ids_step_${step}`] || [] : [];
    const recommendedTexts = stepOneSel ? stepOneSel[`recommended_texts_step_${step}`] || [] : [];
    const recommendedNote = stepOneSel ? stepOneSel[`recommended_note_step_${step}`] || '' : '';
    const prevTitle = prevSel.title.toLowerCase().trim();
    const prevDesc = (prevSel.shade_desc || '').toLowerCase().trim();
    const prevWords = prevTitle.split(/\s+/);
    const prevFirst = prevWords[0];
    let matches = [];

    // Extract skin-tone level from shade_desc (e.g. "is for light skin..." → "light")
    const TONE_WORDS = ['fair', 'light', 'medium', 'dark', 'deep', 'tan', 'rich'];
    let prevTone = '';
    if (prevDesc) {
      for (let tw = 0; tw < TONE_WORDS.length; tw++) {
        const toneRe = new RegExp(`\\b${TONE_WORDS[tw]}\\b`, 'i');
        if (toneRe.test(prevDesc)) {
          prevTone = TONE_WORDS[tw];
          break;
        }
      }
    }
    if (!prevTone) {
      for (let tw2 = 0; tw2 < TONE_WORDS.length; tw2++) {
        const toneRe2 = new RegExp(`\\b${TONE_WORDS[tw2]}\\b`, 'i');
        if (toneRe2.test(prevTitle)) {
          prevTone = TONE_WORDS[tw2];
          break;
        }
      }
    }

    // Strategy 0: explicit recommendations configured on the step-1 variant.
    // This mirrors the legacy bundle stepper: `recommended_variants_step_X`
    // supplies the shade order, and `recommended_variants_label_step_X`
    // supplies the message by matching index.
    if (explicitIds.length > 0) {
      // Resolve recommended IDs across ALL bundle products, not just this step's
      // product. Deep Face Pencil shades recommend a SECOND Face Pencil variant
      // for step 2 (it "doubles as a neutralizing pencil"), which lives in
      // step 1's product — a step-2-only lookup misses it (the bug). Each match
      // carries `_recStep` = the step whose product owns it, so the swatch and
      // the "USE THIS SHADE" swap target the right product.
      const globalById = new Map();
      Object.keys(this.productData).forEach((s) => {
        const p = this.productData[s];
        if (!p || !p.variants) return;
        p.variants.forEach((v) => {
          if (!globalById.has(String(v.id))) globalById.set(String(v.id), { v: v, step: parseInt(s, 10) });
        });
      });
      matches = explicitIds
        .map((id) => globalById.get(String(id)))
        .filter((m) => m && m.v && m.v.available)
        .map((m) => Object.assign({}, m.v, { _recStep: m.step }));
    }

    // Strategy 1: exact or prefix match on title (e.g. "Fair" → "Fair Peach", "Fair Pink")
    if (matches.length === 0) {
      matches = available.filter((v) => {
        const t = v.title.toLowerCase().trim();
        return t === prevTitle || t.indexOf(`${prevTitle} `) === 0;
      });
    }

    // Strategy 2: first-word match on title (e.g. "Light Neutral" → "Light Peach")
    if (matches.length === 0 && prevFirst.length >= 2) {
      matches = available.filter(
        (v) => v.title.toLowerCase().trim().split(/\s+/)[0] === prevFirst
      );
    }

    // Strategy 3: match by skin-tone level in variant titles
    if (matches.length === 0 && prevTone) {
      const toneReMatch = new RegExp(`\\b${prevTone}\\b`, 'i');
      matches = available.filter((v) => toneReMatch.test(v.title));
    }

    // Strategy 4: match by skin-tone level in variant shade_desc
    if (matches.length === 0 && prevTone) {
      const toneReDesc = new RegExp(`\\b${prevTone}\\b`, 'i');
      matches = available.filter((v) => v.shade_desc && toneReDesc.test(v.shade_desc));
    }

    // Strategy 5: any word of prev title matches first word of current variant
    if (matches.length === 0) {
      matches = available.filter((v) => {
        const curFirst = v.title.toLowerCase().trim().split(/\s+/)[0];
        return prevWords.indexOf(curFirst) !== -1 && curFirst.length >= 3;
      });
    }

    // Strategy 6: word-boundary contains on title
    if (matches.length === 0 && prevTitle.length >= 3) {
      const re = new RegExp(`\\b${prevTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      matches = available.filter((v) => re.test(v.title));
    }

    // Fallback: show all variants that have a shade_desc
    if (matches.length === 0) {
      matches = available.filter((v) => v.shade_desc);
    }

    if (matches.length === 0) {
      container.innerHTML = '';
      return;
    }

    // Reference product name (step 1) for contextual text
    const refProductName = this.productData[1] ? this.productData[1].title : '';

    let html = '';
    matches.forEach((match, index) => {
      const matchStep = match._recStep || step;
      const style = this.getSwatchStyle(matchStep, match.id);
      const rawDesc = match.shade_desc || '';
      const recommendedText = recommendedTexts[index]?.recommended_text || recommendedTexts[index] || '';
      let descText = recommendedText;

      if (!descText && rawDesc && /to\s+correct/i.test(rawDesc)) {
        descText = rawDesc.replace(/^is\s+for\s+\w+\s+skin\s+to\s+correct\s*/i, 'If You Have ');
        descText = descText.charAt(0).toUpperCase() + descText.slice(1);
      } else if (!descText && refProductName) {
        descText = `Based on your ${refProductName} shade selection`;
      }

      html += '<div class="bn-stepper__rec-card">';
      html += `<div class="bn-stepper__rec-swatch" style="${style}"></div>`;
      html += '<div class="bn-stepper__rec-info">';
      if (descText) {
        html += `<p class="bn-stepper__rec-text">${bnEscapeHtml(descText)}</p>`;
      }
      html += `<p class="bn-stepper__rec-shade">Recommended for you - <strong>${bnEscapeHtml(match.title)}</strong></p>`;
      html += '</div>';
      const isSelected = this.selections[step]?.variantId === match.id;
      html += `<button type="button" class="bn-stepper__rec-btn${isSelected ? ' active-shade' : ''}" data-bn-rec-use="${match.id}" data-bn-rec-src-step="${matchStep}">USE THIS SHADE</button>`;
      html += '</div>';
    });

    container.innerHTML = html;

    // Recommendation note (e.g. deep shades: "Because Shade 22 contains red
    // pigments, it doubles as a neutralizing pencil…"). Rendered into the
    // bottom-of-panel note container (after the shade picker), matching Main —
    // NOT inside the recommendation box at the top.
    const noteWrap = this.querySelector(`[data-bn-note="${step}"]`);
    if (noteWrap) {
      noteWrap.innerHTML = recommendedNote
        ? '<div class="bn-stepper__rec-note">' +
          '<div class="bn-stepper__rec-note-body"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M7.50004 0.0625C9.47273 0.0625 11.3646 0.846148 12.7595 2.24105C14.1544 3.63595 14.9381 5.52784 14.9381 7.50053C14.9381 9.47322 14.1544 11.3651 12.7595 12.76C11.3646 14.1549 9.47273 14.9386 7.50004 14.9386C5.52735 14.9386 3.63546 14.1549 2.24056 12.76C0.845659 11.3651 0.0620117 9.47322 0.0620117 7.50053C0.0620117 5.52784 0.845659 3.63595 2.24056 2.24105C3.63546 0.846148 5.52735 0.0625 7.50004 0.0625ZM8.61567 4.62912C9.16817 4.62912 9.61654 4.24556 9.61654 3.67712C9.61654 3.10869 9.16711 2.72513 8.61567 2.72513C8.06317 2.72513 7.61692 3.10869 7.61692 3.67712C7.61692 4.24556 8.06317 4.62912 8.61567 4.62912ZM8.81011 10.6078C8.81011 10.4941 8.84942 10.1987 8.8271 10.0309L7.95373 11.036C7.77311 11.2262 7.54679 11.3579 7.44054 11.3229C7.39234 11.3051 7.35205 11.2708 7.32692 11.226C7.30179 11.1812 7.29348 11.1289 7.30348 11.0785L8.75911 6.48C8.87811 5.89669 8.55086 5.36438 7.85704 5.29638C7.12498 5.29638 6.04761 6.03906 5.39204 6.9815C5.39204 7.09413 5.37079 7.37462 5.39311 7.5425L6.26542 6.53631C6.44604 6.34825 6.65642 6.21544 6.76267 6.25156C6.81502 6.27035 6.85791 6.30891 6.88214 6.35897C6.90638 6.40903 6.91002 6.4666 6.89229 6.51931L5.44942 11.0955C5.28261 11.631 5.59817 12.1559 6.36317 12.2749C7.48942 12.2749 8.15454 11.5503 8.81117 10.6078H8.81011Z" fill="black"></path></svg><strong>Note</strong>' +
          '</div>' +
          `<p>${bnEscapeHtml(recommendedNote)}</p></div>`
        : '';
    }
  }

  /* ───────────────────────── Review panel ───────────────────────── */

  populateReview() {
    for (let i = 1; i <= this.totalSteps; i++) {
      const item = this.querySelector(`[data-bn-review-item="${i}"]`);
      if (!item) continue;
      const sel = this.selections[i];
      const swatchEl = item.querySelector('[data-bn-review-swatch]');
      const nameEl = item.querySelector('[data-bn-review-shade-name]');

      // Reflect a cross-product swap (e.g. step 2 → The Face Pencil) in the
      // review heading — the Liquid title is the original product.
      const titleEl = item.querySelector('.bn-stepper__review-title');
      const pd = this.productData[i];
      if (titleEl && pd && pd.title) titleEl.textContent = String(pd.title).toUpperCase();

      if (sel) {
        if (nameEl) nameEl.textContent = sel.title || '';
        if (swatchEl) swatchEl.setAttribute('style', sel.swatchStyle || '');
      } else {
        if (nameEl) nameEl.textContent = 'Not selected';
        if (swatchEl) swatchEl.setAttribute('style', 'background-color:#e5e5e5;');
      }
      this.buildEditOverlay(i, item);
    }
    this.updateAtcState();
  }

  /**
   * Re-drive later steps after the step-1 reference shade is edited in the
   * review. `selectSwatch(1, …)` has already re-applied each step's product
   * swap (e.g. a deep Face Pencil shade turns step 2 into a 2nd Face Pencil);
   * here we re-select each step's RECOMMENDED shade for the new step-1 choice
   * and re-render the review so both the 2nd product AND its shade update.
   * Mirrors live Main, where editing the reference shade cascades downstream.
   */
  _cascadeFromStepOne() {
    const stepOneSel = this.selections[1];
    if (!stepOneSel) return;
    for (let s = 2; s <= this.totalSteps; s++) {
      const recIds = stepOneSel['recommended_variant_ids_step_' + s] || [];
      if (recIds.length) {
        // Re-default to the recommended shade (resolved cross-product if the
        // step swapped to another product). Skips the step-1 cascade itself.
        this.selectSwatch(s, recIds[0], null);
      } else if (!this.selections[s]) {
        // Step swapped product with no explicit recommendation — fall back to
        // its first available variant so the step isn't left unselected.
        const pd = this.productData[s];
        const fav = pd && pd.variants ? (pd.variants.find((v) => v.available) || pd.variants[0]) : null;
        if (fav) this.selectSwatch(s, fav.id, null);
      }
    }
    this.populateReview();
  }

  buildEditOverlay(step, reviewItem) {
    const overlay = reviewItem.querySelector(`[data-bn-edit-overlay="${step}"]`);
    if (!overlay) return;
    const panel = this.querySelector(`[data-bn-panel="${step}"]`);
    if (!panel || panel.dataset.autoVariant) return;

    const pd = this.productData[step];
    if (!pd || !pd.variants) return;

    let html = '<div class="bn-stepper__swatches">';
    pd.variants.forEach((v) => {
      const style = this.getSwatchStyle(step, v.id);
      const sel = this.selections[step];
      const isActive = sel && sel.variantId === v.id;
      html +=
        `<button type="button" class="ph-swatch${isActive ? ' ph-swatch--active' : ''}${!v.available ? ' ph-swatch--unavailable' : ''}"` +
        ` data-bn-edit-swatch="${v.id}"` +
        ` data-bn-edit-step="${step}"` +
        ` aria-label="${bnEscapeAttr(v.title)}"` +
        `${v.available ? '' : ' disabled'}` +
        '>' +
        `<span class="ph-swatch__color" style="${style}"></span>` +
        '</button>';
    });
    html += '</div>';
    html += '<div class="bn-stepper__shade-info">';
    const sel = this.selections[step];
    html += `<span class="bn-stepper__shade-name">${sel ? bnEscapeHtml(sel.title) : ''}</span>`;
    // html += `<span class="bn-stepper__shade-desc">${sel && sel.shade_desc ? bnEscapeHtml(sel.shade_desc) : ''}</span>`;
    html += '</div>';
    overlay.innerHTML = html;
  }

  /* ───────────────────────── Selection ───────────────────────── */

  selectSwatch(step, variantId, srcBtn) {
    const pd = this.productData[step];
    if (!pd) return;
    let v = pd.variants.find((x) => x.id === variantId);
    if (!v) {
      // Cross-product recommendation: a deep Face Pencil shade recommends a
      // SECOND Face Pencil variant for the neutralizer step ("doubles as a
      // neutralizing pencil"). That variant lives in another bundle product, so
      // resolve it from whichever product owns it — otherwise USE THIS SHADE
      // would no-op. The selection / hidden input / cart then carry the correct
      // variant, matching Main's swap behavior.
      const keys = Object.keys(this.productData);
      for (let k = 0; k < keys.length && !v; k++) {
        const p = this.productData[keys[k]];
        if (p && p.variants) v = p.variants.find((x) => x.id === variantId) || v;
      }
    }
    if (!v) return;

    let swatchStyle = '';
    if (srcBtn) {
      const colorSpan = srcBtn.querySelector('.ph-swatch__color');
      if (colorSpan) swatchStyle = colorSpan.getAttribute('style') || '';
    }
    if (!swatchStyle) swatchStyle = this.getSwatchStyle(step, variantId);
    if ((!swatchStyle || swatchStyle === 'background-color:#ccc;') && typeof v.swatch_bg === 'string' && v.swatch_bg) {
      swatchStyle = bnBuildSwatchStyle(v.swatch_bg);
    }

    this.selections[step] = {
      variantId,
      title: v.title,
      shade_desc: v.shade_desc || '',
      swatchStyle,
      image: v.featured_image || '',
      recommended_texts_step_2: v.recommended_texts_step_2 || [],
      recommended_texts_step_3: v.recommended_texts_step_3 || [],
      recommended_texts_step_4: v.recommended_texts_step_4 || [],
      recommended_variant_ids_step_2: v.recommended_variant_ids_step_2 || [],
      recommended_variant_ids_step_3: v.recommended_variant_ids_step_3 || [],
      recommended_variant_ids_step_4: v.recommended_variant_ids_step_4 || [],
      recommended_note_step_2: v.recommended_note_step_2 || '',
      recommended_note_step_3: v.recommended_note_step_3 || '',
      recommended_note_step_4: v.recommended_note_step_4 || '',
    };

    const hiddenInput = document.querySelector(`[data-bn-hidden-input="${step}"]`);
    if (hiddenInput) hiddenInput.value = variantId;

    const panelSwatches = this.querySelectorAll(`[data-bn-swatches="${step}"] [data-bn-swatch]`);
    panelSwatches.forEach((btn) => {
      btn.classList.toggle('ph-swatch--active', parseInt(btn.dataset.bnSwatch, 10) === variantId);
    });

    const recButtons = this.querySelectorAll(`[data-bn-recommendations="${step}"] [data-bn-rec-use]`);
    recButtons.forEach((btn) => {
      btn.classList.toggle('active-shade', parseInt(btn.dataset.bnRecUse, 10) === variantId);
    });

    const shadeInfo = this.querySelector(`[data-bn-shade-info="${step}"]`);
    if (shadeInfo) {
      const nameEl = shadeInfo.querySelector('[data-bn-shade-name]');
      const descEl = shadeInfo.querySelector('[data-bn-shade-desc]');
      if (nameEl) nameEl.textContent = v.title;
      if (descEl) descEl.textContent = v.shade_desc || '';
    }

    const panelImg = this.querySelector(`[data-bn-panel="${step}"] [data-bn-panel-img]`);
    if (panelImg && v.featured_image) panelImg.src = v.featured_image;

    // NOTE: Do NOT mark the step as completed here. The completion fill /
    // animation should only progress when the user explicitly clicks Next
    // (or the review step), not as a side-effect of picking a shade. The
    // showStep() call from _onNextClick already manages is-completed /
    // is-active classes for all indicators.

    this.updateNav(this.currentStep);
    this.updateAtcState();

    // Step 1 is the reference shade that drives every later step's
    // recommendation + product swap. Live Main updates step 2's product the
    // INSTANT you pick the step-1 shade (not on Next), so refresh all
    // downstream steps now — this applies the panel/indicator swap immediately,
    // even while those panels are still hidden.
    if (step === 1) {
      for (let s = 2; s <= this.totalSteps; s++) {
        this.updateRecommended(s);
      }
    }
  }

  updateAtcState() {
    if (!this.atcBtn || this._atcAdding) return;
    let allSelected = true;
    for (let i = 1; i <= this.totalSteps; i++) {
      if (!this.selections[i]) {
        allSelected = false;
        break;
      }
    }
    this.atcBtn.disabled = !allSelected;
  }

  _ownAtcFromInitiator(initiator) {
    if (!this.atcForm || initiator !== this.atcForm) return null;
    return this.atcBtn;
  }

  _setAtcAdding(adding) {
    if (!this.atcBtn) return;
    this._atcAdding = adding;
    if (adding) {
      this.atcBtn.disabled = true;
    } else {
      this._atcAdding = false;
      this.updateAtcState();
    }
  }

  /* ───────────────────────── Add to cart ───────────────────────── */

  /** RFC4122-ish v4 id; crypto.randomUUID when available. */
  _uuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Gather one cart line item per step (deduping repeated variants) plus the
   * `product@qty` conditions string the bundle discount Function expects.
   * Returns null if any step is missing a selection.
   */
  _collectBundleItems() {
    const items = [];
    const conditions = [];
    for (let step = 1; step <= this.totalSteps; step++) {
      const sel = this.selections[step];
      const pd = this.productData[step];
      if (!sel || !pd) return null;
      const productId = Number(pd.id);
      const variantId = Number(sel.variantId);
      if (!variantId) return null;

      const existingCond = conditions.find((c) => c.product === productId);
      if (existingCond) existingCond.quantity += 1;
      else conditions.push({ product: productId, quantity: 1 });

      const existingItem = items.find((it) => it.id === variantId);
      if (existingItem) existingItem.quantity += 1;
      else items.push({ id: variantId, quantity: 1, properties: { _title: sel.title || '' } });
    }
    return { items, conditions };
  }

  /**
   * Add the bundle as N component line items sharing one `_bundle_id` so the
   * cart drawer groups them under the parent and the store discount Function
   * applies the saving. Mirrors Main's productBundleFormNew.createCartItems.
   */
  _onAtcClick(e) {
    if (e) e.preventDefault();
    if (!this.atcBtn || this.atcBtn.disabled || this._atcAdding) return;
    if (!window.liquidAjaxCart || typeof window.liquidAjaxCart.add !== 'function') return;

    const built = this._collectBundleItems();
    if (!built || !built.items.length) return;

    const ds = this.dataset;
    const shared = {
      _bundle_id: this._uuid(),
      _product: ds.bundleTitle || '',
      _image: ds.bundleImage || '',
      _url: ds.bundleUrl || '',
      _message: ds.bundleTitle || '',
      _conditions: built.conditions.map((c) => `${c.product}@${c.quantity}`).join('+'),
      _bundle_quantity: 1,
    };
    if (ds.bundleProductId) shared._product_id = Number(ds.bundleProductId);
    if (ds.bundleDiscountCode) shared._d = ds.bundleDiscountCode;
    if (ds.bundleFreeGiftVariant) shared._free_gift_variant = ds.bundleFreeGiftVariant;

    built.items.forEach((it) => {
      it.properties = { ...it.properties, ...shared };
    });

    // The free gift ships in the SAME add call as the bundle lines — one cart
    // write, no post-add correction and no busy drawer while a second request
    // runs. component-cart-free-gift.js only reconciles drift afterwards.
    const giftVariantId = Number(ds.bundleFreeGiftVariant) || 0;
    if (giftVariantId) {
      const giftProperties = { '_This is a free gift variant': true };
      const giftSettings = window.settings || {};
      if (
        giftSettings.enable_test_promotion &&
        giftSettings.test_promotion_property &&
        giftSettings.test_promotion_property_value
      ) {
        giftProperties[giftSettings.test_promotion_property] = giftSettings.test_promotion_property_value;
      }
      built.items.push({ id: giftVariantId, quantity: 1, properties: giftProperties });
    }

    this._setAtcAdding(true);
    window.liquidAjaxCart.add(
      { items: built.items },
      {
        info: { initiator: 'bundle-new-atc' },
        lastCallback: () => this._setAtcAdding(false),
      }
    );
  }

  _onAjaxStart(e) {
    const rs = e.detail && e.detail.requestState;
    if (!rs || rs.requestType !== 'add') return;
    if (this._ownAtcFromInitiator(rs.info && rs.info.initiator)) {
      this._setAtcAdding(true);
    }
  }

  _onAjaxEnd(e) {
    const rs = e.detail && e.detail.requestState;
    if (!rs || rs.requestType !== 'add') return;
    if (this._ownAtcFromInitiator(rs.info && rs.info.initiator)) {
      this._setAtcAdding(false);
    }
  }

  /* ───────────────────────── Click delegations ───────────────────────── */

  _onStepperClick(e) {
    // Swatch click in step panels
    const swatchBtn = e.target.closest('[data-bn-swatch]');
    if (swatchBtn) {
      e.stopPropagation();
      this.selectSwatch(
        parseInt(swatchBtn.dataset.bnStep, 10),
        parseInt(swatchBtn.dataset.bnSwatch, 10),
        swatchBtn
      );
      return;
    }

    // Edit swatch in review overlay
    const editSwatch = e.target.closest('[data-bn-edit-swatch]');
    if (editSwatch) {
      e.stopPropagation();
      const editStep = parseInt(editSwatch.dataset.bnEditStep, 10);
      const editVid = parseInt(editSwatch.dataset.bnEditSwatch, 10);
      this.selectSwatch(editStep, editVid, editSwatch);
      const reviewItem = this.querySelector(`[data-bn-review-item="${editStep}"]`);
      // Only refresh the overlay (the "below" shade name + active swatch). The
      // summary line `.bn-stepper__review-shade` above the swatches must NOT
      // change on shade click — it reflects the committed selection and is
      // updated when the user clicks SELECT to close the overlay.
      if (reviewItem) this.buildEditOverlay(editStep, reviewItem);
      this.updateAtcState();
      return;
    }

    // Edit / Select toggle — matches live's two-state button:
    //   • closed: "EDIT"   + chevron arrow → opens the overlay
    //   • open:   "SELECT" (no arrow)      → commits selection + closes overlay
    // The swatch click inside the overlay already updates the selection; the
    // SELECT click is what dismisses the overlay so the user explicitly
    // confirms before returning to the review summary.
    const editBtn = e.target.closest('[data-bn-edit]');
    if (editBtn) {
      e.stopPropagation();
      const editStep2 = editBtn.dataset.bnEdit;
      const overlay = this.querySelector(`[data-bn-edit-overlay="${editStep2}"]`);
      if (overlay) {
        const willOpen = overlay.style.display === 'none';
        overlay.style.display = willOpen ? '' : 'none';
        editBtn.classList.toggle('is-open', willOpen);

        const textEl = editBtn.querySelector('.bn-stepper__edit-btn-text');
        if (textEl) textEl.textContent = willOpen ? 'SELECT' : 'EDIT';

        // On commit (SELECT → close), sync the summary line + swatch with the
        // now-confirmed selection. This is the only point the "above" shade
        // name updates — not on each shade click inside the overlay.
        if (!willOpen) {
          const reviewItem = this.querySelector(`[data-bn-review-item="${editStep2}"]`);
          const sel = this.selections[editStep2];
          if (reviewItem && sel) {
            const nameEl = reviewItem.querySelector('[data-bn-review-shade-name]');
            const swatchEl = reviewItem.querySelector('[data-bn-review-swatch]');
            if (nameEl) nameEl.textContent = sel.title || '';
            if (swatchEl) swatchEl.setAttribute('style', sel.swatchStyle || '');
          }
          // Committing an edit to step 1's reference shade re-drives the later
          // steps (2nd product + shade), matching live Main.
          if (String(editStep2) === '1') {
            this._cascadeFromStepOne();
          }
        }
      }
      return;
    }

    // Recommended shade "USE THIS SHADE"
    const recUseBtn = e.target.closest('[data-bn-rec-use]');
    if (recUseBtn) {
      e.stopPropagation();
      const recVid = parseInt(recUseBtn.dataset.bnRecUse, 10);
      const recContainer = recUseBtn.closest('[data-bn-recommendations]');
      if (recContainer && !Number.isNaN(recVid)) {
        const recStep = parseInt(recContainer.dataset.bnRecommendations, 10);
        if (!Number.isNaN(recStep)) this.selectSwatch(recStep, recVid, null);
      }
      return;
    }

    // Step indicators are intentionally NOT clickable — users must drive
    // progression via the Previous / Next buttons. Leaving the click handler
    // here would allow skipping to any step (forward or backward) by clicking
    // the numbered circles, which conflicts with the live theme's behavior.
  }

  /* ───────────────────────── Hover preview ───────────────────────── */

  _onStepperMouseover(e) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const swatchBtn = e.target.closest('[data-bn-swatch]');
    if (!swatchBtn) return;
    const step = parseInt(swatchBtn.dataset.bnStep, 10);
    const vid = parseInt(swatchBtn.dataset.bnSwatch, 10);
    const pd = this.productData[step];
    if (!pd) return;
    const v = pd.variants.find((x) => x.id === vid);
    if (!v) return;

    const shadeInfo = this.querySelector(`[data-bn-shade-info="${step}"]`);
    if (shadeInfo) {
      const nameEl = shadeInfo.querySelector('[data-bn-shade-name]');
      const descEl = shadeInfo.querySelector('[data-bn-shade-desc]');
      if (nameEl) nameEl.textContent = v.title;
      if (descEl) descEl.textContent = v.shade_desc || '';
    }
    const panelImg = this.querySelector(`[data-bn-panel="${step}"] [data-bn-panel-img]`);
    if (panelImg && v.featured_image) panelImg.src = v.featured_image;
  }

  _onStepperMouseout(e) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const swatchBtn = e.target.closest('[data-bn-swatch]');
    if (!swatchBtn) return;
    const step = parseInt(swatchBtn.dataset.bnStep, 10);
    const sel = this.selections[step];

    // Only fall back to the selected shade's info / image on mouseout. If the
    // user hasn't selected a shade yet we deliberately keep the last hovered
    // values on screen — clearing them would make the description flicker
    // out every time the cursor crosses the gap between two swatches, which
    // is jarring (see screenshot bug: "shade desc disappears between hovers").
    // The next swatch the cursor enters will overwrite the text via the
    // mouseover handler, so nothing persists incorrectly.
    if (!sel) return;

    const shadeInfo = this.querySelector(`[data-bn-shade-info="${step}"]`);
    if (shadeInfo) {
      const nameEl = shadeInfo.querySelector('[data-bn-shade-name]');
      const descEl = shadeInfo.querySelector('[data-bn-shade-desc]');
      if (nameEl) nameEl.textContent = sel.title;
      if (descEl) descEl.textContent = sel.shade_desc || '';
    }
    if (sel.image) {
      const panelImg = this.querySelector(`[data-bn-panel="${step}"] [data-bn-panel-img]`);
      if (panelImg) panelImg.src = sel.image;
    }
  }

  /* ───────────────────────── Navigation buttons ───────────────────────── */

  _onPrevClick() {
    if (this.currentStep === 'review') this.showStep(this.totalSteps);
    else if (this.currentStep > 1) this.showStep(this.currentStep - 1);
  }

  _onNextClick() {
    if (typeof this.currentStep === 'number' && this.currentStep < this.totalSteps) {
      this.showStep(this.currentStep + 1);
    } else if (this.currentStep === this.totalSteps) {
      this.showStep('review');
    }
  }

  _onScrollClick() {
    // Compute a top offset that accounts for the sticky header (and the
    // announcement bar, when present) so the stepper's "Choose Your Shades"
    // heading isn't hidden under it after the smooth scroll.
    //
    // The header on this theme uses position: sticky on the OUTER section
    // wrapper (#shopify-section-{header.id}) and on-scroll-up animates the
    // wrapper's `top` value, so #main-header's bounding rect can momentarily
    // report 0 / a translated position. We measure the outer section first
    // (most reliable), then fall back to #main-header, then to a generous
    // hard-coded floor so the heading is never tucked behind the chrome.
    const headerSection =
      document.querySelector('#shopify-section-header, [id^="shopify-section-"][id*="header"]') ||
      null;
    const innerHeader = document.getElementById('main-header');
    const announcement = document.querySelector(
      'announcement-bar-section, .announcement-bar, .shopify-section-announcement, [id^="shopify-section-"][id*="announcement"]'
    );

    const headerH = Math.max(
      headerSection ? headerSection.getBoundingClientRect().height : 0,
      innerHeader ? innerHeader.getBoundingClientRect().height : 0
    );
    const announceH = announcement ? announcement.getBoundingClientRect().height : 0;

    // Generous breathing room (matches what live theme does — heading lands
    // ~30px below the chrome). Floor at 140px so we never land behind the
    // sticky chrome if measurements failed.
    const extraGap = 30;
    const measured = headerH + announceH + extraGap;
    const floor = window.innerWidth <= 749 ? 110 : 160;
    const offset = Math.max(measured, floor);

    const targetTop = this.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
  }

  /* ───────────────────────── Document delegations (siblings) ───────────────────────── */

  _onDocNotifyClick(e) {
    const close = e.target.closest('[data-ph-notify-close]');
    if (!close) return;
    const modal = close.closest('[data-ph-notify-modal]');
    if (modal) modal.setAttribute('hidden', '');
  }
}

if (!customElements.get('product-bundle-new')) {
  customElements.define('product-bundle-new', ProductBundleNew);
}
